import {Store} from '../../features/stores/models/store.model';
import {StoreDTO} from '../../features/stores/models/store.dto';
import {mapStoreDTOToModel} from '../../features/stores/mappers/store.mapper';
import {LoggerService} from '../common/LoggerService';

export interface CoordinateTransformerService {
  transformStoreToMapStore(storeDto: StoreDTO): Store;
  validateStoreCoordinates(store: Store): boolean;
  addMapAliases(store: Store): Store & { lat: number; lng: number };
  isValidLatitude(value: any): boolean;
  isValidLongitude(value: any): boolean;
  isValidCoordinatePair(lat: any, lng: any): boolean;
  sanitizeCoordinates(lat: any, lng: any): { lat: number; lng: number };
  validateAndLogCoordinates(store: Store, context: string): void;
}

export class DefaultCoordinateTransformerService implements CoordinateTransformerService {
  private static instance: DefaultCoordinateTransformerService;

  private constructor () {}

  public static getInstance (): DefaultCoordinateTransformerService {
    if (!DefaultCoordinateTransformerService.instance) {
      DefaultCoordinateTransformerService.instance = new DefaultCoordinateTransformerService();
    }
    return DefaultCoordinateTransformerService.instance;
  }

  transformStoreToMapStore (storeDto: StoreDTO): Store {
    const store = mapStoreDTOToModel(storeDto);
    this.validateAndLogCoordinates(store, 'StoreMapper');
    return store;
  }

  validateStoreCoordinates (store: Store): boolean {
    return this.isValidCoordinatePair(store.latitude, store.longitude);
  }

  addMapAliases (store: Store): Store & { lat: number; lng: number } {
    const sanitizedCoords = this.sanitizeCoordinates(store.latitude, store.longitude);
    return {
      ...store,
      lat: sanitizedCoords.lat,
      lng: sanitizedCoords.lng
    };
  }

  isValidLatitude (value: any): boolean {
    return value !== undefined &&
           value !== null &&
           !isNaN(value) &&
           typeof value === 'number' &&
           value >= -90 &&
           value <= 90;
  }

  isValidLongitude (value: any): boolean {
    return value !== undefined &&
           value !== null &&
           !isNaN(value) &&
           typeof value === 'number' &&
           value >= -180 &&
           value <= 180;
  }

  isValidCoordinatePair (lat: any, lng: any): boolean {
    return this.isValidLatitude(lat) && this.isValidLongitude(lng);
  }

  sanitizeCoordinates (lat: any, lng: any): { lat: number; lng: number } {
    return {
      lat: this.isValidLatitude(lat) ? lat : 0,
      lng: this.isValidLongitude(lng) ? lng : 0
    };
  }

  validateAndLogCoordinates (store: Store, context: string): void {
    if (!this.isValidCoordinatePair(store.latitude, store.longitude)) {
      LoggerService.warn(`${context}: Invalid coordinates for store`, {
        id: store.id,
        name: store.name,
        latitude: store.latitude,
        longitude: store.longitude
      });
    }
  }
}

// Default export for easier importing
export default DefaultCoordinateTransformerService;
